// src/components/asset/AssetHeader.tsx

import { Box, Typography, IconButton, Tooltip, useTheme, useMediaQuery, Menu, MenuItem, ListItemIcon } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/Download";
import PreviewIcon from "@mui/icons-material/OpenInNew";
import MoreVertIcon from '@mui/icons-material/MoreVert'; // The "kebab" menu icon
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RoleBasedAccess from "../../services/RoleBasedAccess";
import { downloadFilesAsync } from "../../services/FileDownloader";
import { useAsset } from "../../context/AssetContext";
import React, { useState } from "react"; // Import React and useState
import { buildIframePreviewPath } from "./assetHeaderHelpers";

const AssetHeader: React.FC = () => {
  const theme = useTheme();
  // 👇 1. Detect if we are on a mobile screen
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    asset,
    openEditAssetIdModal,
    openEditAssetModal,
    previousAssetTarget,
    nextAssetTarget,
    goToPreviousAsset,
    goToNextAsset,
    isAdjacentLoading,
    isAssetNavigationLoading,
  } = useAsset();

  // 👇 2. State for the mobile actions menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  if (!asset) return null;

  const iframePreviewPath = buildIframePreviewPath(asset.systemCode, asset.assetInstanceId || asset.assetId);
  const openIframePreview = () => {
    if (!iframePreviewPath) return;
    window.open(iframePreviewPath, "_blank", "noopener,noreferrer");
  };

  return (
    <Box
      sx={{
        borderBottom: `solid 5px ${theme.palette.divider}`,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        // 👇 3. Use space-between on mobile for a simpler layout
        justifyContent: isMobile ? 'space-between' : 'center',
        position: "relative", // Keep relative for desktop's absolute positioning
      }}
    >
      <Box sx={{ width: isMobile ? '40px' : '120px', display: 'flex' }} />

      {/* --- ASSET ID + NAV --- */}
      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="h5" noWrap>
            {asset.assetId}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title={previousAssetTarget ? "Previous asset" : "No previous asset"}>
              <span>
                <IconButton
                  data-cy="asset-prev-btn"
                  onClick={goToPreviousAsset}
                  disabled={!previousAssetTarget || isAdjacentLoading || isAssetNavigationLoading}
                  size="small"
                >
                  <ChevronLeftIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={nextAssetTarget ? "Next asset" : "No next asset"}>
              <span>
                <IconButton
                  data-cy="asset-next-btn"
                  onClick={goToNextAsset}
                  disabled={!nextAssetTarget || isAdjacentLoading || isAssetNavigationLoading}
                  size="small"
                >
                  <ChevronRightIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Tooltip title={previousAssetTarget ? "Previous asset" : "No previous asset"}>
            <span>
              <IconButton
                data-cy="asset-prev-btn"
                onClick={goToPreviousAsset}
                disabled={!previousAssetTarget || isAdjacentLoading || isAssetNavigationLoading}
                size="small"
              >
                <ChevronLeftIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Typography variant="body2" sx={{ maxWidth: 180 }} noWrap color={previousAssetTarget ? "text.primary" : "text.disabled"}>
            {previousAssetTarget?.asset.assetId || "No previous"}
          </Typography>

          <Box sx={{ height: 26, borderLeft: `1px solid ${theme.palette.divider}`, mx: 0.5 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Typography variant="h4" noWrap>
              {asset.assetId}
            </Typography>
            <RoleBasedAccess allowedRoles={["Admin", "Support"]}>
              <Tooltip title="Edit Asset ID">
                <IconButton data-cy="asset-edit-id-btn" onClick={openEditAssetIdModal}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
            </RoleBasedAccess>
          </Box>

          <Box sx={{ height: 26, borderLeft: `1px solid ${theme.palette.divider}`, mx: 0.5 }} />

          <Typography variant="body2" sx={{ maxWidth: 180 }} noWrap color={nextAssetTarget ? "text.primary" : "text.disabled"}>
            {nextAssetTarget?.asset.assetId || "No next"}
          </Typography>
          <Tooltip title={nextAssetTarget ? "Next asset" : "No next asset"}>
            <span>
              <IconButton
                data-cy="asset-next-btn"
                onClick={goToNextAsset}
                disabled={!nextAssetTarget || isAdjacentLoading || isAssetNavigationLoading}
                size="small"
              >
                <ChevronRightIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}

      {/* 👇 4. Conditionally render the action icons */}
      {isMobile ? (
        // --- MOBILE ACTIONS MENU ---
        <>
          <IconButton data-cy="asset-mobile-actions-trigger" onClick={handleMenuOpen}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
          >
            {/* We can use RoleBasedAccess INSIDE the menu */}
            <RoleBasedAccess allowedRoles={["Admin", "Support"]}>
              <MenuItem data-cy="asset-mobile-edit-id-item" onClick={() => { openEditAssetIdModal(); handleMenuClose(); }}>
                <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                Edit Asset ID
              </MenuItem>
            </RoleBasedAccess>
            <RoleBasedAccess allowedRoles={["Admin"]}>
              <MenuItem data-cy="asset-mobile-preview-iframe-item" onClick={() => { openIframePreview(); handleMenuClose(); }}>
                <ListItemIcon><PreviewIcon fontSize="small" /></ListItemIcon>
                Preview Iframe
              </MenuItem>
            </RoleBasedAccess>
            <RoleBasedAccess allowedRoles={["Admin", "Support", "Auditor"]}>
               <MenuItem data-cy="asset-mobile-edit-asset-item" onClick={() => { openEditAssetModal(); handleMenuClose(); }}>
                <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                Edit Asset
              </MenuItem>
            </RoleBasedAccess>
            <MenuItem data-cy="asset-mobile-download-item" onClick={() => { downloadFilesAsync(asset); handleMenuClose(); }}>
              <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
              Download Images
            </MenuItem>
          </Menu>
        </>
      ) : (
        // --- DESKTOP ACTIONS (Unchanged) ---
        <Box
          sx={{
            position: "absolute",
            right: 16,
            top: "50%",
            transform: "translateY(-50%)",
            display: "flex",
            gap: 1,
            alignItems: "center",
          }}
        >
          <RoleBasedAccess allowedRoles={["Admin"]}>
            <Tooltip title="Preview Iframe">
              <span>
                <IconButton
                  data-cy="asset-preview-iframe-btn"
                  sx={{ backgroundColor: theme.palette.info.main }}
                  onClick={openIframePreview}
                  disabled={!iframePreviewPath}
                >
                  <PreviewIcon />
                </IconButton>
              </span>
            </Tooltip>
          </RoleBasedAccess>
          <RoleBasedAccess allowedRoles={["Admin", "Support", "Auditor"]}>
            <Tooltip title="Edit Asset">
              <IconButton data-cy="asset-edit-btn" sx={{ backgroundColor: theme.palette.primary.main }} onClick={openEditAssetModal}>
                <EditIcon />
              </IconButton>
            </Tooltip>
          </RoleBasedAccess>

          <Tooltip title="Download Images">
            <IconButton data-cy="asset-download-btn" sx={{ backgroundColor: theme.palette.error.main }} onClick={() => downloadFilesAsync(asset)}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

export default AssetHeader;
